import { useEffect, useRef, useState } from 'react';
import { useAuthImage } from '../lib/authImage';

export interface QrImageProps {
  /** Ruta de qr-api que entrega la imagen (`/v1/qrs/{code}/image?...` o `/v1/operator/qrs/{id}/image?...`). */
  path: string;
  alt: string;
  orgId?: string;
  /** Carga solo al entrar en pantalla (miniaturas de listas). */
  lazy?: boolean;
  className?: string;
}

/** QR servido por qr-api con sesión: skeleton cuadrado mientras carga y "Reintentar" si falla (R14). */
export function QrImage({ path, alt, orgId, lazy = false, className = '' }: QrImageProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(!lazy);

  useEffect(() => {
    if (!lazy || visible) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setVisible(true);
        io.disconnect();
      }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [lazy, visible]);

  const { url, loading, error, retry } = useAuthImage(path, { orgId, enabled: visible });

  return (
    <div ref={ref} className={`relative aspect-square overflow-hidden rounded-md border border-line bg-white ${className}`}>
      {url ? (
        <img src={url} alt={alt} className="h-full w-full object-contain" />
      ) : error ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1 p-1 text-center text-xs text-ink-muted" role="alert">
          <span>No pudimos cargar el QR</span>
          <button type="button" onClick={retry} className="font-semibold text-green underline">Reintentar</button>
        </div>
      ) : (
        <div className={`h-full w-full bg-gray-100 ${loading ? 'animate-pulse motion-reduce:animate-none' : ''}`} role="status" aria-label={`Cargando ${alt}`} />
      )}
    </div>
  );
}
